// Angular
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import {
  Component,
  Inject,
  ViewEncapsulation,
  HostListener,
  ElementRef,
  OnDestroy,
  Renderer2,
  Injector,
  ChangeDetectionStrategy,
	signal,
	OnInit
} from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, BehaviorSubject, Subscription, Subject } from 'rxjs';

import { Guest } from './models';
import * as olciUtil from './util';
import * as olci from './actions/olci';
import * as fromRoot from './reducers';
import * as tracking from './actions/tracking';
import { CONSTANTS } from './constants/constants';
import {AppComponentPathInterface, TelemetryErrorData} from './shared/interfaces';
import { LoaderService } from './modules/loading/loader.service';
import { ErrorService } from './modules/core/services/error.service';
import { BookingService } from './modules/core/services/booking.service';
import { GatingServices } from './modules/core/services/gating.services';
import {AppTitlePathUtils, ObjectAppTitleKey} from './utils/app-title-path.utils';
import { ResourceBundleService } from './modules/core/services/resource-bundle.service';
import { ScriptInjectorComponent } from './components/script-injector/script-injector.component';
import { SessionExpirationModalService } from './shared/services/session-expiration-modal/session-expiration-modal.service';
import { BRAND, LANGUAGE, environment } from '~environments/environment';
import * as utils from 'src/app/util';

declare let window: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
      '(window:resize)': 'onWindowResize($event)',
      '(window:load)': 'onWindowLoad($event)'
    },
    standalone: false
})
export class AppComponent extends ScriptInjectorComponent implements OnInit, OnDestroy {
	selectedGuest$: Observable<Guest>;

	window: any;
	routerEvents: Subscription;
	public _bundleSubscription: Subscription;
	public _bundle: any;
	brandCorpName: any;
	brandName: string;
	aboutUsPath: string;
	public year: any;
	defaultBundle: BehaviorSubject<any>;
	headersRequest: any;
	private previousPage = '';
	private readonly destroy$ = new Subject<void>();

  private loader: LoaderService;
  private titleService: Title;
  public location: Location;
  public elementRef: ElementRef;
  public bookingService: BookingService;
  private gatingService: GatingServices;
  private errorService: ErrorService;
  public resourceBundleService: ResourceBundleService;
  public sessionExpirationModalService: SessionExpirationModalService;

	public language = signal<string>('');
	public brand = signal<string>('');
	public envName = signal<string>('');

	constructor(
    public router: Router,
    private renderer: Renderer2,
    private readonly injector : Injector,
	private readonly store: Store<fromRoot.AppState>,
	@Inject(CONSTANTS) private readonly constants: any,
    @Inject('UrlService') public urlService: any,
	) {
		super(renderer);
        this.injectServices();

		this.brandName = this.bookingService.getBrand();
		// At the beginning of app execution, API_GET_SESSION_END is not called
		this.bookingService.sessionIsEnded = false;

		this.selectedGuest$ = store.pipe(select(fromRoot.getEntitySelectedGuest));

		this._bundleSubscription = this.resourceBundleService.bundle.subscribe(bundle => (this._bundle = bundle));

		this.year = new Date().getFullYear();
		let currentUrl = this.router.url;
		this.routerEvents = this.router.events.subscribe(event => {
      const flagNavigationStart = event instanceof NavigationStart;
      if (flagNavigationStart && !event.url.startsWith('/error')) {
        olciUtil.writeLocalStorage('referrer', event.url.split('?')[0]);
      }

      if (flagNavigationStart && event.url !== '/resretrieval') {
        this.loader.showLoader();
      }

			if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
				if (event.url !== currentUrl) {
					this.previousPage = currentUrl !== '/' ? currentUrl : 'beforeyoucruise';
					currentUrl = event.url;
				}
				window.sessionStorage.setItem(this.constants.SESSION_STORAGE_KEYS.PREVIOUS_PAGE, this.previousPage);
			}

      if (event instanceof NavigationEnd) {
        const path = this.location.path();
        const { analyticsPath, titlePath } = this.createPaths(path);

        // analytics
        // delay to make sure the completed guest list is updated in the store on the confirmation page before sending analytics.
        // Don't remove this code.
        this.dispatchTrackingAccordingAnalyticsPath(analyticsPath);

        // set page title using Angular Title service
        const brandCorpName = this.bookingService.getBrandCorpName();
        const titlePrefix = brandCorpName + ': Check-in: ';
        this.titleService.setTitle(this.createTitlePath(titlePath, titlePrefix));
      }
		});
		this.trackNullValuesOnOlciDataCookie();
	}

	override ngOnInit(): void {
		super.ngOnInit(); 
		this.initGlobalLoginConfig();
	}

	private initGlobalLoginConfig() {
		this.language.set(LANGUAGE);
		this.brand.set(BRAND);
		const envName = utils.returnEnvironment(environment?.envName || '');
		this.envName.set(envName);
	}

  injectServices() {
    this.loader = this.injector.get<LoaderService>(LoaderService);
    this.titleService = this.injector.get<Title>(Title);
    this.location = this.injector.get<Location>(Location);
    this.elementRef = this.injector.get<ElementRef>(ElementRef);
    this.bookingService = this.injector.get<BookingService>(BookingService);
    this.gatingService = this.injector.get<GatingServices>(GatingServices);
    this.errorService = this.injector.get<ErrorService>(ErrorService);
    this.renderer = this.injector.get<Renderer2>(Renderer2);
    this.resourceBundleService = this.injector.get<ResourceBundleService>(ResourceBundleService);
    this.sessionExpirationModalService = this.injector.get<SessionExpirationModalService>(SessionExpirationModalService);
  }

  private createPaths(path: string): AppComponentPathInterface {
    const analyticsPath = path.indexOf('/') === 0
      ? path.replace('/', '').split('?')[0]
      : path.split('?')[0];

    const titlePath = analyticsPath.indexOf('/') !== -1
      ? analyticsPath.replace('/', ':').split(':')[1]
      : analyticsPath;

    return {
      analyticsPath, titlePath
    }
  }

  private createTitlePath(titlePath: string, titlePrefix: string) {
    return titlePrefix + AppTitlePathUtils[titlePath as ObjectAppTitleKey];
  }

  private dispatchTrackingAccordingAnalyticsPath(analyticsPath: string) {
    if (analyticsPath === 'confirmation') {
      setTimeout(() => {
        this.store.dispatch(new tracking.TrackPageViewAction());
      }, 4000);
    } else if (
      analyticsPath !== 'resretrieval' &&
      analyticsPath !== 'error' &&
      analyticsPath !== this.constants.PAGE_NAME_FOR_TRACKING.GUEST_DETAILS
    ) {
      this.store.dispatch(new tracking.TrackPageViewAction());
    }
  }

	get footerBrandName() {
		switch (this.bookingService.getBrand()) {
			case this.constants.BRANDS_NAMES.ROYAL:
				return 'Royal Caribbean International';

			case this.constants.BRANDS_NAMES.CELEBRITY:
				return 'Celebrity Cruises, Inc.'
		}
		return ''
	}

	ngOnDestroy() {
		this.routerEvents.unsubscribe();
		this._bundleSubscription.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	@HostListener('window:resize', ['$event'])
	onWindowResize(event: any) {
		const screenwidth = event.target.innerWidth; // window width
		let data = { isphone: false, isdesktop: false };
		if (screenwidth >= this.constants.OLCI_BREAKPOINTS.PHONE) {
			data = { isphone: false, isdesktop: true };
			this.store.dispatch(new olci.UpdateScreenSizeAtion(data));
		}
		if (screenwidth < this.constants.OLCI_BREAKPOINTS.PHONE) {
			data = { isphone: true, isdesktop: false };
			this.store.dispatch(new olci.UpdateScreenSizeAtion(data));
		}
		window.sessionStorage.setItem(this.constants.SESSION_STORAGE_KEYS.SCREEN_WIDTH, `desktop_layout:${screenwidth}x${event.target.innerHeight}`);
	}

	@HostListener('window:load', ['$event']) // window load event here only fires on entering OLCI, not navigating inside OLCI.
	onWindowLoad(event: any) {
		const screenwidth = window.innerWidth; // window width
		let data = { isphone: false, isdesktop: false };
		if (screenwidth >= this.constants.OLCI_BREAKPOINTS.PHONE) {
			data = { isphone: false, isdesktop: true };
		}
		if (screenwidth < this.constants.OLCI_BREAKPOINTS.PHONE) {
			data = { isphone: true, isdesktop: false };
		}
		window.sessionStorage.setItem(this.constants.SESSION_STORAGE_KEYS.SCREEN_WIDTH, `desktop_layout:${screenwidth}x${window.innerHeight}`);
		this.store.dispatch(new olci.UpdateScreenSizeAtion(data));
	}

  @HostListener('window:beforeunload')
	onWindowBeforeUnload() {
		if (!this.bookingService.sessionIsEnded) {
			this.gatingService.clearSessionStorage();
		}
	}

	skipToContent(event: any) {
		event.preventDefault();
		if (document.getElementById('rcl-sub-nav')) {
			document.getElementById('rcl-sub-nav')?.focus();
		} else if (document.getElementsByTagName('header').length > 1) {
			document.getElementsByTagName('header')[1].focus();
		} else if (document.getElementById('rcl-boarding-pass')) {
			document.getElementById('rcl-boarding-pass')?.focus();
		} else if (document.getElementById('ssp-wte-margin')) {
			document.getElementById('ssp-wte-margin')?.focus();
		}
	}

	public openLegalModalDialog(event: any): void {
		event.preventDefault();
		this.store.dispatch(new olci.OpenModalAction({ name: this.constants.MODAL.TERMS_OF_USE }));
	}

	trackNullValuesOnOlciDataCookie(): void {
		const rawOlciDataCookie = olciUtil.getDecodedAccessToken(this.constants.COOKIES.OLCI.OLCI_DATA);
		const nullValueError = window.sessionStorage.getItem(this.constants.SESSION_STORAGE_KEYS.COOKIE_NULL_VALUES_ERROR_LOGGED);

		if (this.gatingService.hasCookieNullValues(rawOlciDataCookie) && !nullValueError || nullValueError === 'false') {
			const url = `${window.location.origin}/check-in/olci/version`;
			const message = this.constants.ERROR_MESSAGES.ERROR_OLCI_DATA_COOKIE;
			const developerMessage = this.constants.COOKIES.OLCI.OLCI_DATA;
			const telemetryErrorData = {
				message,
				lookup: 'GC-000-UNKNOWN',
			} as TelemetryErrorData;

			this.errorService.defaultTelemetryHttpCatch(
				{ message, developerMessage},
				telemetryErrorData,
				url
            );
			window.location.href = this.gatingService.getGAAccountURL();
		}
	}
}
